import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLang } from '../context/LanguageContext';
import ArtistFirstWinPanel from '../components/onboarding/ArtistFirstWinPanel';
import { API_BASE as API } from '../lib/apiBase';
import SongUploadWizard from '../components/songs/SongUploadWizard';
import {
  User, Mail, Shield, LogOut, LayoutDashboard, BarChart3, ListMusic, Clock,
  Camera, Save, Lock, Heart, Music, Edit, Upload, Plus, Trash2, X, Check, Sparkles, Loader, Wallet, Video, Radio, Library, MessageCircle
} from 'lucide-react';


export default function ProfilePage() {
  const { user, token, logout, updateUser } = useAuth();
  const { t, lang } = useLang();
  const location = useLocation();
  const [editing, setEditing] = useState(false);
  const [changingPass, setChangingPass] = useState(false);
  const [form, setForm] = useState({});
  const [passForm, setPassForm] = useState({ current_password: '', new_password: '', confirm: '' });
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [favorites, setFavorites] = useState([]);
  const [followingArtists, setFollowingArtists] = useState([]);
  const [artistProfile, setArtistProfile] = useState(null);
  const [editingArtist, setEditingArtist] = useState(false);
  const [artistForm, setArtistForm] = useState({});
  const [showSongUpload, setShowSongUpload] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [aiPrompt, setAiPrompt] = useState('');
  const [showAiGen, setShowAiGen] = useState(false);
  const [artistCopilotPrompt, setArtistCopilotPrompt] = useState('');
  const [generatingArtistCopy, setGeneratingArtistCopy] = useState(false);
  const [showVideoUpload, setShowVideoUpload] = useState(false);
  const [videoForm, setVideoForm] = useState({ title: '', url: '' });
  const [uploadingVideo, setUploadingVideo] = useState(false);
  const [legalAcceptances, setLegalAcceptances] = useState({});
  const [artistTermsSummary, setArtistTermsSummary] = useState('');
  const [verificationHub, setVerificationHub] = useState({ providers: {}, connections: [] });
  const [verificationLoading, setVerificationLoading] = useState(false);
  const [myInquireOffers, setMyInquireOffers] = useState([]);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (!token) return;
    fetch(`${API}/api/booking-offers?limit=30`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.json())
      .then((d) => setMyInquireOffers(d.offers || []))
      .catch(() => setMyInquireOffers([]));
  }, [token]);

  useEffect(() => {
    if (user && token) {
      setForm({ name: user.name || '', photo: user.photo || '', bio: user.bio || '', city: user.city || '', country: user.country || '', favorite_genres: user.favorite_genres || [] });
      fetchFavorites();
      fetchFollowingArtists();
      if (user.role === 'artist') {
        fetchArtistProfile();
        fetchLegalState();
        fetchVerificationHub();
      }
    }
  }, [user, token]);

  useEffect(() => {
    if (!user || !token || user.role !== 'artist' || !location.search) return;
    const params = new URLSearchParams(location.search);
    const provider = params.get('verification_provider');
    if (!provider) return;
    const connected = params.get('verification_connected') === '1';
    const error = params.get('verification_error');
    if (connected) {
      setMessage(lang === 'es' ? `Conexión ${provider} completada. Relanzamos tus verificaciones pendientes.` : `${provider} connection completed. Pending verifications are being rerun.`);
      fetchVerificationHub();
      fetchArtistProfile();
      setTimeout(() => fetchArtistProfile(), 3500);
    } else if (error) {
      setMessage(`${provider}: ${decodeURIComponent(error)}`);
    }
    window.history.replaceState({}, '', location.pathname);
  }, [location.search, user, token, lang]);

  useEffect(() => {
    if (user?.role !== 'artist' || !artistProfile?.id) return;
    if (location.hash !== '#upload-song') return;
    setShowSongUpload(true);
    requestAnimationFrame(() => {
      document.getElementById('upload-song')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  }, [location.hash, user?.role, artistProfile?.id]);

  const fetchLegalState = async () => {
    try {
      const [acceptRes, artistDocRes] = await Promise.all([
        fetch(`${API}/api/legal/acceptances/me`, { headers: { Authorization: `Bearer ${token}` } }).then(r => r.json()),
        fetch(`${API}/api/legal/documents/artist_onboarding_terms?lang=${lang}`).then(r => r.json()),
      ]);
      setLegalAcceptances(acceptRes.acceptances || {});
      setArtistTermsSummary((artistDocRes.sections || []).slice(0, 2).map(item => `${item.heading}: ${item.content}`).join(' '));
    } catch (e) {}
  };

  const acceptArtistTerms = async () => {
    try {
      await fetch(`${API}/api/legal/accept`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ slug: 'artist_onboarding_terms', signer_name: user?.name || '', context: 'artist_profile_gate' })
      });
      setMessage(lang === 'es' ? 'Condiciones de artista aceptadas' : 'Artist terms accepted');
      fetchLegalState();
    } catch (e) { setMessage('Error'); }
  };

  const fetchFavorites = async () => {
    try {
      const res = await fetch(`${API}/api/users/favorites`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      setFavorites(data.favorites || []);
    } catch (e) {}
  };

  const fetchFollowingArtists = async () => {
    try {
      const res = await fetch(`${API}/api/users/following`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      setFollowingArtists(data.following || []);
    } catch (e) {}
  };

  const fetchArtistProfile = async () => {
    try {
      const res = await fetch(`${API}/api/artists/me`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      if (data.exists !== false) {
        setArtistProfile(data);
        setArtistForm({
          name: data.name || '', genre: (data.genre || []).join(', '), bio_es: data.bio_es || '',
          bio_en: data.bio_en || '', photo: data.photo || '', cover: data.cover || '', tagline: data.tagline || '',
          price_per_show: data.price_per_show || 0, city: data.city || '', country: data.country || '', available: data.available !== false,
          event_types: (data.event_types || []).join(', '), instagram: data.instagram || '', tiktok: data.tiktok || '', website: data.website || '', youtube: data.youtube || ''
        });
      }
    } catch (e) {}
  };

  const fetchVerificationHub = async () => {
    try {
      const res = await fetch(`${API}/api/verification/me/connections`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      setVerificationHub(data || { providers: {}, connections: [] });
    } catch (e) {}
  };

  const saveProfile = async () => {
    setSaving(true);
    setMessage('');
    try {
      const res = await fetch(`${API}/api/users/profile`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(form)
      });
      const data = await res.json();
      updateUser(data);
      setForm({ name: data.name || '', photo: data.photo || '', bio: data.bio || '', city: data.city || '', country: data.country || '', favorite_genres: data.favorite_genres || [] });
      setMessage(lang === 'es' ? 'Perfil actualizado' : 'Profile updated');
      setEditing(false);
    } catch (e) { setMessage('Error'); }
    setSaving(false);
  };

  const changePassword = async () => {
    if (passForm.new_password !== passForm.confirm) {
      setMessage(lang === 'es' ? 'Las contrasenas no coinciden' : 'Passwords do not match');
      return;
    }
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/users/password`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ current_password: passForm.current_password, new_password: passForm.new_password })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail);
      setMessage(lang === 'es' ? 'Contrasena cambiada' : 'Password changed');
      setChangingPass(false);
      setPassForm({ current_password: '', new_password: '', confirm: '' });
    } catch (e) { setMessage(e.message); }
    setSaving(false);
  };

  const uploadImage = async (e, target) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append('file', file);
    try {
      const res = await fetch(`${API}/api/upload/image`, { method: 'POST', body: fd });
      const data = await res.json();
      if (target === 'profile') {
        setForm(f => ({ ...f, photo: data.url }));
        updateUser({ photo: data.url });
      }
      else if (target === 'artist_photo') setArtistForm(f => ({ ...f, photo: data.url }));
      else if (target === 'artist_cover') setArtistForm(f => ({ ...f, cover: data.url }));
    } catch (e) {}
    setUploading(false);
  };

  const generateAiImage = async (target) => {
    if (!aiPrompt.trim()) return;
    setGenerating(true);
    try {
      const res = await fetch(`${API}/api/ai/generate-image`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ prompt: aiPrompt, target: target === 'profile' ? 'user' : target })
      });
      const data = await res.json();
      if (data.url) {
        if (target === 'artist_photo') setArtistForm(f => ({ ...f, photo: data.url }));
        else if (target === 'artist_cover') setArtistForm(f => ({ ...f, cover: data.url }));
        else if (target === 'profile') {
          setForm(f => ({ ...f, photo: data.url }));
          updateUser({ photo: data.url });
        }
        setMessage(lang === 'es' ? 'Imagen generada con IA' : 'AI image generated');
      } else {
        setMessage(data.detail || 'Error');
      }
    } catch (e) { setMessage('Error generando imagen'); }
    setGenerating(false);
    setShowAiGen(false);
    setAiPrompt('');
  };

  const uploadVideo = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploadingVideo(true);
    const fd = new FormData();
    fd.append('file', file);
    try {
      const res = await fetch(`${API}/api/upload/video`, { method: 'POST', body: fd });
      const data = await res.json();
      setVideoForm(f => ({ ...f, url: data.url }));
    } catch (e) {}
    setUploadingVideo(false);
  };

  const saveVideo = async () => {
    if (!videoForm.url || !artistProfile) return;
    const videos = [...(artistProfile.videos || []), { title: videoForm.title, url: videoForm.url }];
    await fetch(`${API}/api/artists/me`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ videos })
    });
    setArtistProfile(p => ({ ...p, videos }));
    setShowVideoUpload(false);
    setVideoForm({ title: '', url: '' });
    setMessage(lang === 'es' ? 'Video subido' : 'Video uploaded');
  };

  const saveArtistProfile = async () => {
    setSaving(true);
    try {
      const body = {
        ...artistForm,
        genre: artistForm.genre.split(',').map(g => g.trim()).filter(Boolean),
        event_types: (artistForm.event_types || '').split(',').map(g => g.trim()).filter(Boolean)
      };
      await fetch(`${API}/api/artists/me`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(body)
      });
      setMessage(lang === 'es' ? 'Perfil de artista actualizado' : 'Artist profile updated');
      setEditingArtist(false);
      fetchArtistProfile();
    } catch (e) { setMessage('Error'); }
    setSaving(false);
  };

  const generateArtistCopy = async () => {
    if (!artistCopilotPrompt.trim()) return;
    setGeneratingArtistCopy(true);
    setMessage('');
    try {
      const res = await fetch(`${API}/api/ai/artist-copilot`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          prompt: artistCopilotPrompt,
          artist_name: artistForm.name,
          city: artistForm.city,
          genres: artistForm.genre.split(',').map(g => g.trim()).filter(Boolean)
        })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Error');
      setArtistForm((f) => ({
        ...f,
        tagline: data.tagline || f.tagline,
        bio_es: data.bio_es || f.bio_es,
        bio_en: data.bio_en || f.bio_en,
        event_types: (data.event_types || []).join(', ') || f.event_types,
      }));
      if (data.visual_prompt) setAiPrompt(data.visual_prompt);
      setMessage(lang === 'es' ? 'Texto creativo preparado. Si quieres, usa el prompt visual para generar imagen.' : 'Creative copy prepared. Use the visual prompt if you want to generate an image.');
    } catch (e) {
      setMessage(e.message || 'Error');
    }
    setGeneratingArtistCopy(false);
  };

  const startOwnershipConnection = async (provider) => {
    setVerificationLoading(true);
    try {
      const res = await fetch(`${API}/api/verification/oauth/${provider}/start`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Error');
      window.location.href = data.auth_url;
      return;
    } catch (e) {
      setMessage(e.message || 'Error');
    }
    setVerificationLoading(false);
  };

  const rerunOwnershipVerification = async (songId) => {
    try {
      await fetch(`${API}/api/verification/songs/${songId}/rerun`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ reason: 'artist_profile_rerun' })
      });
      setMessage(lang === 'es' ? 'Verificación relanzada.' : 'Verification rerun.');
      fetchArtistProfile();
      setTimeout(() => fetchArtistProfile(), 3500);
    } catch (e) {
      setMessage(e.message || 'Error');
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen pt-14 flex flex-col items-center justify-center gap-4">
        <p className="text-pulse-muted">{lang === 'es' ? 'Inicia sesion para ver tu perfil' : 'Login to view your profile'}</p>
        <Link to="/login" className="bg-pulse-primary text-black px-6 py-2 rounded-xl font-semibold">{t('auth.login')}</Link>
      </div>
    );
  }

  const photoUrl = form.photo || user.photo;

  return (
    <div data-testid="profile-page" className="min-h-screen pt-14 pb-32">
      <div className="max-w-2xl mx-auto px-4 py-6">
        {message && (
          <div className="mb-4 p-3 bg-pulse-primary/10 border border-pulse-primary/20 rounded-xl text-sm text-pulse-primary flex items-center gap-2 animate-fade-in-up">
            <Check size={14} /> {message}
            <button onClick={() => setMessage('')} className="ml-auto"><X size={14} /></button>
          </div>
        )}

        {/* Profile Header */}
        <div className="mb-8 rounded-[32px] border border-white/10 bg-gradient-to-br from-white/[0.05] to-transparent p-6">
          <div className="text-center">
          <div className="relative inline-block">
            {photoUrl ? (
              <img src={photoUrl} alt={user.name} className="w-24 h-24 rounded-full object-cover border-2 border-pulse-border" />
            ) : (
              <div className="w-24 h-24 rounded-full bg-pulse-surface border border-pulse-border flex items-center justify-center">
                <User size={36} className="text-pulse-muted" />
              </div>
            )}
            {editing && (
              <button
                onClick={() => fileInputRef.current?.click()}
                className="absolute bottom-0 right-0 w-8 h-8 rounded-full bg-pulse-primary flex items-center justify-center text-black"
              >
                <Camera size={14} />
              </button>
            )}
            <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={(e) => uploadImage(e, 'profile')} />
          </div>
          <h1 className="font-heading font-bold text-xl mt-3">{user.name}</h1>
          <p className="text-sm text-pulse-muted flex items-center justify-center gap-1 mt-1"><Mail size={12} /> {user.email}</p>
          <div className="flex items-center justify-center gap-1 mt-2">
            <Shield size={12} className="text-pulse-primary" />
            <span className="text-xs text-pulse-primary capitalize">{user.role}</span>
          </div>
          </div>

          <div className="grid md:grid-cols-2 gap-3 mt-6">
            <div data-testid="user-identity-card" className="rounded-2xl border border-pulse-border bg-pulse-surface p-4 text-left">
              <p className="text-[10px] uppercase tracking-[0.2em] text-pulse-muted mb-2">{lang === 'es' ? 'Tu lado usuario' : 'Your listener side'}</p>
              <h2 className="font-heading font-semibold text-lg mb-1">{lang === 'es' ? 'Tu perfil de usuario' : 'Your user profile'}</h2>
              <p className="text-sm text-pulse-muted">{lang === 'es' ? 'Colección, artistas guardados, artistas que sigues y tus movimientos dentro de PulseMusic.' : 'Collection, saved artists, followed artists and your activity inside PulseMusic.'}</p>
            </div>
            <div data-testid="artist-identity-card" className="rounded-2xl border border-pulse-border bg-pulse-surface p-4 text-left">
              <p className="text-[10px] uppercase tracking-[0.2em] text-pulse-muted mb-2">{lang === 'es' ? 'Tu lado artista' : 'Your artist side'}</p>
              <h2 className="font-heading font-semibold text-lg mb-1">{artistProfile?.id ? (lang === 'es' ? 'Tu perfil artista' : 'Your artist profile') : (lang === 'es' ? 'Aún no has activado tu perfil artista' : 'Your artist profile is not active yet')}</h2>
              <p className="text-sm text-pulse-muted">{artistProfile?.id ? (lang === 'es' ? 'Desde aquí editas tu imagen pública, canciones, bio y preview real.' : 'From here you edit your public image, songs, bio and real preview.') : (lang === 'es' ? 'Cuando quieras compartir música, te llevamos directo a ese espacio.' : 'Whenever you want to share music, we take you straight into that space.')}</p>
              <div className="flex flex-wrap gap-2 mt-4">
                {artistProfile?.id ? (
                  <>
                    <Link data-pressable="true" to={`/artist/${artistProfile.id}`} className="rounded-full bg-pulse-primary px-4 py-2 text-xs font-semibold text-black">
                      {lang === 'es' ? 'Ver mi perfil público' : 'See my public profile'}
                    </Link>
                    <button onClick={() => document.getElementById('artist-profile-anchor')?.scrollIntoView({ behavior: 'smooth' })} className="rounded-full border border-pulse-border px-4 py-2 text-xs text-white/80">
                      {lang === 'es' ? 'Ir a mi perfil artista' : 'Go to my artist profile'}
                    </button>
                  </>
                ) : (
                  <Link data-pressable="true" to="/onboarding" className="rounded-full bg-pulse-primary px-4 py-2 text-xs font-semibold text-black">
                    {lang === 'es' ? 'Crear mi perfil artista' : 'Create my artist profile'}
                  </Link>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Edit Profile Form */}
        {editing ? (
          <div className="bg-pulse-surface border border-pulse-border rounded-xl p-4 mb-6 space-y-3 animate-fade-in-up">
            <h3 className="font-heading font-semibold text-sm mb-2">{lang === 'es' ? 'Editar Perfil' : 'Edit Profile'}</h3>
            <input
              data-testid="edit-name"
              value={form.name}
              onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
              placeholder={t('auth.name')}
              className="w-full bg-pulse-elevated border border-pulse-border rounded-lg px-4 py-2.5 text-sm focus:ring-2 focus:ring-cyan-500 focus:outline-none"
            />
            <textarea
              value={form.bio || ''}
              onChange={e => setForm(f => ({ ...f, bio: e.target.value }))}
              placeholder={lang === 'es' ? 'Bio / Sobre ti' : 'Bio / About you'}
              rows={2}
              className="w-full bg-pulse-elevated border border-pulse-border rounded-lg px-4 py-2.5 text-sm focus:ring-2 focus:ring-cyan-500 focus:outline-none resize-none"
            />
            <div className="grid grid-cols-2 gap-3">
              <input value={form.city || ''} onChange={e => setForm(f => ({ ...f, city: e.target.value }))} placeholder={lang === 'es' ? 'Ciudad' : 'City'} className="bg-pulse-elevated border border-pulse-border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-cyan-500 focus:outline-none" />
              <input value={form.country || ''} onChange={e => setForm(f => ({ ...f, country: e.target.value }))} placeholder={lang === 'es' ? 'Pais' : 'Country'} className="bg-pulse-elevated border border-pulse-border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-cyan-500 focus:outline-none" />
            </div>
            {form.photo && (
              <div className="flex items-center gap-2 text-xs text-pulse-muted">
                <img src={form.photo} alt="" className="w-8 h-8 rounded object-cover" />
                <span>{lang === 'es' ? 'Foto actualizada' : 'Photo updated'}</span>
              </div>
            )}
            <div className="rounded-xl border border-pulse-primary/20 bg-pulse-primary/5 p-3 space-y-3">
              <p className="text-xs text-pulse-primary flex items-center gap-2"><Sparkles size={12} /> {lang === 'es' ? 'Generar imagen con IA' : 'Generate image with AI'}</p>
              <input data-testid="profile-ai-prompt" value={aiPrompt} onChange={e => setAiPrompt(e.target.value)} placeholder={lang === 'es' ? 'Describe tu energía, estilo o estética visual' : 'Describe your energy, style or visual aesthetic'} className="w-full bg-pulse-elevated border border-pulse-border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-cyan-500 focus:outline-none" />
              <button data-testid="generate-profile-ai-image" onClick={() => generateAiImage('profile')} disabled={generating || !aiPrompt.trim()} className="rounded-full bg-pulse-primary px-4 py-2 text-xs font-semibold text-black disabled:opacity-50 inline-flex items-center gap-2">
                {generating ? <Loader size={12} className="animate-spin" /> : <Sparkles size={12} />} {lang === 'es' ? 'Generar avatar' : 'Generate avatar'}
              </button>
            </div>
            <div className="flex gap-2">
              <button onClick={saveProfile} disabled={saving} className="bg-pulse-primary text-black px-4 py-2 rounded-lg text-sm font-semibold flex items-center gap-1 disabled:opacity-50">
                <Save size={14} /> {t('common.save')}
              </button>
              <button onClick={() => setEditing(false)} className="text-pulse-muted px-4 py-2 text-sm">{t('common.cancel')}</button>
            </div>
          </div>
        ) : (
          <button
            data-testid="edit-profile-btn"
            onClick={() => setEditing(true)}
            className="w-full mb-4 flex items-center justify-center gap-2 p-3 bg-pulse-surface border border-pulse-border rounded-xl text-sm hover:border-cyan-500/30 transition-colors duration-200"
          >
            <Edit size={14} /> {lang === 'es' ? 'Editar Perfil' : 'Edit Profile'}
          </button>
        )}

        {/* Change Password */}
        {changingPass ? (
          <div className="bg-pulse-surface border border-pulse-border rounded-xl p-4 mb-6 space-y-3 animate-fade-in-up">
            <h3 className="font-heading font-semibold text-sm flex items-center gap-2"><Lock size={14} /> {lang === 'es' ? 'Cambiar Contrasena' : 'Change Password'}</h3>
            <input type="password" value={passForm.current_password} onChange={e => setPassForm(f => ({ ...f, current_password: e.target.value }))} placeholder={lang === 'es' ? 'Contrasena actual' : 'Current password'} className="w-full bg-pulse-elevated border border-pulse-border rounded-lg px-4 py-2.5 text-sm focus:ring-2 focus:ring-cyan-500 focus:outline-none" />
            <input type="password" value={passForm.new_password} onChange={e => setPassForm(f => ({ ...f, new_password: e.target.value }))} placeholder={lang === 'es' ? 'Nueva contrasena' : 'New password'} className="w-full bg-pulse-elevated border border-pulse-border rounded-lg px-4 py-2.5 text-sm focus:ring-2 focus:ring-cyan-500 focus:outline-none" />
            <input type="password" value={passForm.confirm} onChange={e => setPassForm(f => ({ ...f, confirm: e.target.value }))} placeholder={lang === 'es' ? 'Confirmar contrasena' : 'Confirm password'} className="w-full bg-pulse-elevated border border-pulse-border rounded-lg px-4 py-2.5 text-sm focus:ring-2 focus:ring-cyan-500 focus:outline-none" />
            <div className="flex gap-2">
              <button onClick={changePassword} disabled={saving} className="bg-pulse-primary text-black px-4 py-2 rounded-lg text-sm font-semibold disabled:opacity-50">{t('common.save')}</button>
              <button onClick={() => setChangingPass(false)} className="text-pulse-muted px-4 py-2 text-sm">{t('common.cancel')}</button>
            </div>
          </div>
        ) : (
          <button onClick={() => setChangingPass(true)} className="w-full mb-4 flex items-center justify-center gap-2 p-3 bg-pulse-surface border border-pulse-border rounded-xl text-sm hover:border-cyan-500/30 transition-colors duration-200">
            <Lock size={14} /> {lang === 'es' ? 'Cambiar Contrasena' : 'Change Password'}
          </button>
        )}

        {/* Artist Section */}
        {user.role === 'artist' && (
          <div id="artist-profile-anchor" className="mb-6">
            <h2 className="font-heading font-bold text-lg mb-1 flex items-center gap-2">
              <Music size={18} className="text-pulse-primary" />
              {lang === 'es' ? 'Mi Perfil de Artista' : 'My Artist Profile'}
            </h2>
            <p className="text-xs text-pulse-muted mb-4">
              <Link to="/artist-booking" data-testid="profile-link-artist-booking" className="text-pulse-primary font-semibold hover:underline">
                {lang === 'es' ? 'Sistema de contratación' : 'Booking system'}
              </Link>
              {' '}
              {lang === 'es' ? '— configura ofertas y publicación' : '— offers & publication'}
            </p>

            {legalAcceptances.artist_onboarding_terms && !legalAcceptances.artist_onboarding_terms.accepted && (
              <div data-testid="artist-legal-gate" className="mb-4 rounded-2xl border border-yellow-500/30 bg-yellow-500/10 p-4 space-y-3">
                <div className="flex items-start gap-3">
                  <Shield size={16} className="text-yellow-300 mt-0.5" />
                  <div>
                    <p className="font-semibold text-sm">{lang === 'es' ? 'Antes de operar como artista' : 'Before operating as artist'}</p>
                    <p className="text-xs text-pulse-muted mt-1">{artistTermsSummary}</p>
                  </div>
                </div>
                <button data-testid="artist-accept-terms-button" onClick={acceptArtistTerms} className="bg-pulse-primary text-black px-4 py-2 rounded-lg text-sm font-semibold">{lang === 'es' ? 'Aceptar condiciones de artista' : 'Accept artist terms'}</button>
              </div>
            )}

            {editingArtist ? (
              <div className="bg-pulse-surface border border-pulse-border rounded-xl p-4 space-y-4 animate-fade-in-up">
                <div className="rounded-2xl border border-white/10 bg-white text-black p-4 shadow-[0_18px_60px_rgba(255,255,255,0.08)]">
                  <p className="text-[11px] uppercase tracking-[0.22em] text-black/45 mb-2">{lang === 'es' ? 'Asistente creativo' : 'Creative copilot'}</p>
                  <div className="space-y-3">
                    <textarea
                      data-testid="artist-copilot-prompt"
                      value={artistCopilotPrompt}
                      onChange={e => setArtistCopilotPrompt(e.target.value)}
                      placeholder={lang === 'es' ? 'Describe el universo del artista: estilo, emoción, tipo de público, estética o tipo de evento que quieres atraer.' : 'Describe the artist universe: sound, emotion, audience, visual style or type of event you want to attract.'}
                      rows={3}
                      className="w-full rounded-2xl border border-black/10 bg-white px-4 py-3 text-sm text-black placeholder:text-black/35 focus:ring-2 focus:ring-black/10 focus:outline-none resize-none"
                    />
                    <div className="flex flex-wrap gap-2">
                      <button data-testid="artist-copilot-generate-copy" onClick={generateArtistCopy} disabled={generatingArtistCopy || !artistCopilotPrompt.trim()} className="rounded-full bg-black px-4 py-2 text-xs font-semibold text-white disabled:opacity-50 inline-flex items-center gap-2">
                        {generatingArtistCopy ? <Loader size={12} className="animate-spin" /> : <Sparkles size={12} />} {lang === 'es' ? 'Generar bio y enfoque' : 'Generate bio and angle'}
                      </button>
                      <button onClick={() => setShowAiGen(showAiGen === 'artist_cover' ? false : 'artist_cover')} className="rounded-full border border-black/10 px-4 py-2 text-xs text-black/75 inline-flex items-center gap-2">
                        <Sparkles size={12} /> {lang === 'es' ? 'Generar imagen' : 'Generate image'}
                      </button>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <input value={artistForm.name} onChange={e => setArtistForm(f => ({...f, name: e.target.value}))} placeholder={lang === 'es' ? 'Nombre artistico' : 'Artist name'} className="bg-pulse-elevated border border-pulse-border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-cyan-500 focus:outline-none" />
                  <input value={artistForm.genre} onChange={e => setArtistForm(f => ({...f, genre: e.target.value}))} placeholder={lang === 'es' ? 'Generos (coma sep)' : 'Genres (comma sep)'} className="bg-pulse-elevated border border-pulse-border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-cyan-500 focus:outline-none" />
                  <input value={artistForm.tagline || ''} onChange={e => setArtistForm(f => ({...f, tagline: e.target.value}))} placeholder={lang === 'es' ? 'Titular corto del artista' : 'Short artist headline'} className="bg-pulse-elevated border border-pulse-border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-cyan-500 focus:outline-none col-span-2" />
                  <input value={artistForm.bio_es} onChange={e => setArtistForm(f => ({...f, bio_es: e.target.value}))} placeholder="Bio (ES)" className="bg-pulse-elevated border border-pulse-border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-cyan-500 focus:outline-none col-span-2" />
                  <input value={artistForm.bio_en} onChange={e => setArtistForm(f => ({...f, bio_en: e.target.value}))} placeholder="Bio (EN)" className="bg-pulse-elevated border border-pulse-border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-cyan-500 focus:outline-none col-span-2" />
                  <input type="number" value={artistForm.price_per_show} onChange={e => setArtistForm(f => ({...f, price_per_show: parseFloat(e.target.value)||0}))} placeholder={lang === 'es' ? 'Precio/Show' : 'Price/Show'} className="bg-pulse-elevated border border-pulse-border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-cyan-500 focus:outline-none" />
                  <input value={artistForm.city} onChange={e => setArtistForm(f => ({...f, city: e.target.value}))} placeholder={lang === 'es' ? 'Ciudad' : 'City'} className="bg-pulse-elevated border border-pulse-border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-cyan-500 focus:outline-none" />
                  <input value={artistForm.country || ''} onChange={e => setArtistForm(f => ({...f, country: e.target.value}))} placeholder={lang === 'es' ? 'Pais' : 'Country'} className="bg-pulse-elevated border border-pulse-border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-cyan-500 focus:outline-none" />
                  <input value={artistForm.event_types || ''} onChange={e => setArtistForm(f => ({...f, event_types: e.target.value}))} placeholder={lang === 'es' ? 'Tipos de evento (coma sep)' : 'Event types (comma sep)'} className="bg-pulse-elevated border border-pulse-border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-cyan-500 focus:outline-none" />
                  <input value={artistForm.instagram || ''} onChange={e => setArtistForm(f => ({...f, instagram: e.target.value}))} placeholder="Instagram URL" className="bg-pulse-elevated border border-pulse-border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-cyan-500 focus:outline-none" />
                  <input value={artistForm.tiktok || ''} onChange={e => setArtistForm(f => ({...f, tiktok: e.target.value}))} placeholder="TikTok URL" className="bg-pulse-elevated border border-pulse-border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-cyan-500 focus:outline-none" />
                  <input value={artistForm.website || ''} onChange={e => setArtistForm(f => ({...f, website: e.target.value}))} placeholder={lang === 'es' ? 'Web oficial' : 'Official website'} className="bg-pulse-elevated border border-pulse-border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-cyan-500 focus:outline-none" />
                  <input value={artistForm.youtube || ''} onChange={e => setArtistForm(f => ({...f, youtube: e.target.value}))} placeholder="YouTube URL" className="bg-pulse-elevated border border-pulse-border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-cyan-500 focus:outline-none" />
                </div>
                <div className="flex gap-3 flex-wrap">
                  <button onClick={() => { const inp = document.createElement('input'); inp.type='file'; inp.accept='image/*'; inp.onchange=(ev)=>uploadImage(ev,'artist_photo'); inp.click(); }} className="text-xs bg-pulse-elevated border border-pulse-border px-3 py-1.5 rounded-lg flex items-center gap-1">
                    <Camera size={12} /> {lang === 'es' ? 'Subir Foto' : 'Upload Photo'}
                  </button>
                  <button onClick={() => { const inp = document.createElement('input'); inp.type='file'; inp.accept='image/*'; inp.onchange=(ev)=>uploadImage(ev,'artist_cover'); inp.click(); }} className="text-xs bg-pulse-elevated border border-pulse-border px-3 py-1.5 rounded-lg flex items-center gap-1">
                    <Upload size={12} /> {lang === 'es' ? 'Subir Cover' : 'Upload Cover'}
                  </button>
                  <button onClick={() => setShowAiGen(showAiGen === 'artist_photo' ? false : 'artist_photo')} className="text-xs bg-pulse-primary/10 border border-pulse-primary/20 text-pulse-primary px-3 py-1.5 rounded-lg flex items-center gap-1">
                    <Sparkles size={12} /> {lang === 'es' ? 'IA Foto' : 'AI Photo'}
                  </button>
                  <button onClick={() => setShowAiGen(showAiGen === 'artist_cover' ? false : 'artist_cover')} className="text-xs bg-pulse-primary/10 border border-pulse-primary/20 text-pulse-primary px-3 py-1.5 rounded-lg flex items-center gap-1">
                    <Sparkles size={12} /> {lang === 'es' ? 'IA Cover' : 'AI Cover'}
                  </button>
                </div>
                {showAiGen && (
                  <div className="bg-pulse-elevated border border-pulse-primary/20 rounded-lg p-3 space-y-2 animate-fade-in-up">
                    <p className="text-xs text-pulse-primary flex items-center gap-1"><Sparkles size={12} /> {lang === 'es' ? 'Generar imagen con IA' : 'Generate image with AI'}</p>
                    <input
                      data-testid="ai-image-prompt"
                      value={aiPrompt}
                      onChange={e => setAiPrompt(e.target.value)}
                      placeholder={lang === 'es' ? 'Describe la imagen que quieres...' : 'Describe the image you want...'}
                      className="w-full bg-pulse-surface border border-pulse-border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-cyan-500 focus:outline-none"
                    />
                    <div className="flex gap-2">
                      <button
                        data-testid="ai-generate-btn"
                        onClick={() => generateAiImage(showAiGen)}
                        disabled={generating || !aiPrompt.trim()}
                        className="bg-pulse-primary text-black px-4 py-1.5 rounded-lg text-xs font-semibold disabled:opacity-50 flex items-center gap-1"
                      >
                        {generating ? <Loader size={12} className="animate-spin" /> : <Sparkles size={12} />}
                        {generating ? (lang === 'es' ? 'Generando...' : 'Generating...') : (lang === 'es' ? 'Generar' : 'Generate')}
                      </button>
                      <button onClick={() => { setShowAiGen(false); setAiPrompt(''); }} className="text-pulse-muted text-xs px-3">{t('common.cancel')}</button>
                    </div>
                  </div>
                )}
                <div className="grid sm:grid-cols-2 gap-3">
                  {artistForm.photo && <img src={artistForm.photo} alt="" className="w-full h-40 rounded-2xl object-cover border border-pulse-border" />}
                  {artistForm.cover && <img src={artistForm.cover} alt="" className="w-full h-40 rounded-2xl object-cover border border-pulse-border" />}
                </div>
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input type="checkbox" checked={artistForm.available} onChange={e => setArtistForm(f => ({...f, available: e.target.checked}))} className="accent-pulse-primary" />
                  {lang === 'es' ? 'Disponible para eventos' : 'Available for events'}
                </label>
                <div className="flex gap-2">
                  <button onClick={saveArtistProfile} disabled={saving} className="bg-pulse-primary text-black px-4 py-2 rounded-lg text-sm font-semibold flex items-center gap-1 disabled:opacity-50"><Save size={14} /> {t('common.save')}</button>
                  <button onClick={() => setEditingArtist(false)} className="text-pulse-muted px-4 py-2 text-sm">{t('common.cancel')}</button>
                </div>
              </div>
            ) : (
              <button onClick={() => setEditingArtist(true)} className="w-full flex items-center justify-center gap-2 p-3 bg-pulse-surface border border-pulse-border rounded-xl text-sm hover:border-cyan-500/30 transition-colors duration-200 mb-4">
                <Edit size={14} /> {lang === 'es' ? 'Editar Perfil de Artista' : 'Edit Artist Profile'}
              </button>
            )}

            <ArtistFirstWinPanel
              lang={lang}
              songs={artistProfile?.songs || []}
              artistPublicId={artistProfile?.id}
              className="mb-6"
              density="compact"
            />

            <div className="bg-pulse-surface border border-pulse-border rounded-2xl p-4 mb-4 space-y-4">
              <div>
                <p className="text-xs uppercase tracking-[0.2em] text-pulse-muted">{lang === 'es' ? 'Ownership signals' : 'Ownership signals'}</p>
                <h3 className="font-heading font-semibold mt-2">{lang === 'es' ? 'Conecta tus perfiles para reforzar ownership' : 'Connect profiles to strengthen ownership'}</h3>
                <p className="text-xs text-pulse-muted mt-2">{lang === 'es' ? 'Spotify y Google/YouTube suman señales al trust score. Si faltan claves del sistema, la subida seguirá funcionando en modo degradado.' : 'Spotify and Google/YouTube add signals to the trust score. If system keys are missing, uploads still work in degraded mode.'}</p>
              </div>

              <div className="grid sm:grid-cols-2 gap-3">
                {['spotify', 'google'].map((provider) => {
                  const providerConfig = verificationHub.providers?.[provider] || {};
                  const connection = (verificationHub.connections || []).find(item => item.provider === provider);
                  const isConnected = Boolean(connection);
                  return (
                    <div key={provider} data-testid={`ownership-provider-${provider}`} className="rounded-2xl border border-pulse-border bg-pulse-elevated/70 p-4">
                      <div className="flex items-center justify-between gap-3 mb-2">
                        <div>
                          <p className="font-semibold text-sm">{provider === 'spotify' ? 'Spotify' : 'Google / YouTube'}</p>
                          <p className="text-[11px] text-pulse-muted mt-1">
                            {isConnected
                              ? (lang === 'es' ? 'Cuenta conectada' : 'Connected account')
                              : providerConfig.configured
                                ? (lang === 'es' ? 'Disponible para conectar' : 'Ready to connect')
                                : (lang === 'es' ? 'Modo degradado: falta configuración' : 'Degraded mode: missing config')}
                          </p>
                        </div>
                        <span className={`text-[10px] px-2.5 py-1 rounded-full ${isConnected ? 'bg-green-500/15 text-green-300' : providerConfig.configured ? 'bg-cyan-500/15 text-cyan-200' : 'bg-pulse-bg text-pulse-muted border border-pulse-border'}`}>
                          {isConnected ? 'ON' : (providerConfig.configured ? 'READY' : 'OFF')}
                        </span>
                      </div>
                      {connection && (
                        <p data-testid={`ownership-provider-connected-${provider}`} className="text-xs text-white/80 mb-3">{connection.display_name || connection.email || connection.channel_title}</p>
                      )}
                      <button
                        data-testid={`ownership-connect-${provider}`}
                        onClick={() => startOwnershipConnection(provider)}
                        disabled={verificationLoading || !providerConfig.configured}
                        className="w-full rounded-xl bg-pulse-primary text-black font-semibold px-4 py-2.5 text-sm disabled:opacity-40"
                      >
                        {isConnected ? (lang === 'es' ? 'Reconectar' : 'Reconnect') : (lang === 'es' ? 'Conectar' : 'Connect')}
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Upload Song */}
            <div id="upload-song" className="scroll-mt-24" />
            <button onClick={() => setShowSongUpload(!showSongUpload)} className="w-full flex items-center justify-center gap-2 p-3 bg-pulse-primary/10 border border-pulse-primary/20 rounded-xl text-sm text-pulse-primary hover:bg-pulse-primary/20 transition-colors duration-200 mb-4">
              <Upload size={14} /> {lang === 'es' ? 'Subir Cancion' : 'Upload Song'}
            </button>

            {showSongUpload && (
              <div className="mb-4 animate-fade-in-up">
                <SongUploadWizard
                  token={token}
                  userName={user?.name || ''}
                  onSuccess={() => {
                    setShowSongUpload(false);
                    setMessage(lang === 'es' ? 'Canción enviada. Verificación en curso.' : 'Track submitted. Verification in progress.');
                    fetchArtistProfile();
                    setTimeout(() => fetchArtistProfile(), 3500);
                  }}
                  onCancel={() => setShowSongUpload(false)}
                />
              </div>
            )}

            {/* Artist Songs */}
            {artistProfile?.songs?.length > 0 && (
              <div className="space-y-2">
                <h3 className="text-sm text-pulse-muted">{lang === 'es' ? 'Mis Canciones' : 'My Songs'} ({artistProfile.songs.length})</h3>
                {artistProfile.songs.map(s => (
                  <div key={s.id} data-testid={`artist-song-${s.id}`} className="flex items-center gap-3 p-3 bg-pulse-surface border border-pulse-border rounded-2xl text-sm">
                    {s.cover && <img src={s.cover} alt="" className="w-8 h-8 rounded object-cover" />}
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <p className="truncate">{s.title_es}</p>
                        <span data-testid={`artist-song-status-${s.id}`} className={`text-[10px] px-2 py-0.5 rounded-full ${s.status === 'approved' ? 'bg-green-500/15 text-green-300' : s.status === 'rejected' ? 'bg-red-500/15 text-red-300' : s.status === 'pending_review' ? 'bg-cyan-500/15 text-cyan-200' : 'bg-amber-500/15 text-amber-300'}`}>{s.status || 'approved'}</span>
                      </div>
                      <p className="text-xs text-pulse-muted">{s.plays} plays · Trust {s.trust_score ?? 0}{s.isrc ? ` · ISRC ${s.isrc}` : ''}</p>
                      <p className="text-[11px] text-pulse-muted mt-1">{(s.verification?.summary || [lang === 'es' ? 'Esperando señales de ownership.' : 'Waiting for ownership signals.']).slice(0, 2).join(' · ')}</p>
                    </div>
                    <button data-testid={`artist-song-rerun-${s.id}`} onClick={() => rerunOwnershipVerification(s.id)} className="px-3 py-2 rounded-xl border border-pulse-border text-xs text-pulse-muted hover:text-white hover:border-cyan-500/30">
                      {lang === 'es' ? 'Reintentar' : 'Rerun'}
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* Upload Video */}
            <button onClick={() => setShowVideoUpload(!showVideoUpload)} className="w-full flex items-center justify-center gap-2 p-3 bg-red-500/10 border border-red-500/20 rounded-xl text-sm text-red-400 hover:bg-red-500/20 transition-colors duration-200">
              <Video size={14} /> {lang === 'es' ? 'Subir Videoclip' : 'Upload Video'}
            </button>
            {showVideoUpload && (
              <div className="bg-pulse-elevated border border-pulse-border rounded-xl p-4 space-y-3 animate-fade-in-up">
                <input value={videoForm.title} onChange={e => setVideoForm(f => ({ ...f, title: e.target.value }))} placeholder={lang === 'es' ? 'Titulo del video' : 'Video title'} className="w-full bg-pulse-surface border border-pulse-border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-red-500 focus:outline-none" />
                {videoForm.url ? (
                  <div className="rounded-lg overflow-hidden border border-pulse-border"><video src={videoForm.url} controls className="w-full aspect-video" /></div>
                ) : (
                  <label className="block cursor-pointer">
                    <div className="border-2 border-dashed border-pulse-border rounded-xl p-8 text-center hover:border-red-500/30 transition-colors">
                      <Video size={24} className="mx-auto mb-2 text-pulse-muted" />
                      <p className="text-xs text-pulse-muted">{uploadingVideo ? (lang === 'es' ? 'Subiendo...' : 'Uploading...') : (lang === 'es' ? 'Click para seleccionar video' : 'Click to select video')}</p>
                    </div>
                    <input type="file" accept="video/*" onChange={uploadVideo} className="hidden" />
                  </label>
                )}
                <div className="flex gap-2">
                  <button onClick={saveVideo} disabled={!videoForm.url} className="bg-red-500 text-white px-4 py-2 rounded-lg text-sm font-semibold disabled:opacity-50">{t('common.save')}</button>
                  <button onClick={() => { setShowVideoUpload(false); setVideoForm({ title: '', url: '' }); }} className="text-pulse-muted px-4 py-2 text-sm">{t('common.cancel')}</button>
                </div>
              </div>
            )}

            {/* Artist Videos */}
            {artistProfile?.videos?.length > 0 && (
              <div className="space-y-2">
                <h3 className="text-sm text-pulse-muted">{lang === 'es' ? 'Mis Videoclips' : 'My Videos'} ({artistProfile.videos.length})</h3>
                {artistProfile.videos.map((v, i) => (
                  <div key={i} className="rounded-xl overflow-hidden border border-pulse-border bg-black">
                    <video src={v.url} controls className="w-full aspect-video" />
                    {v.title && <p className="p-2 text-xs">{v.title}</p>}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {myInquireOffers.length > 0 && (
          <div className="mb-4 bg-pulse-surface border border-cyan-500/20 rounded-2xl p-4">
            <h2 className="text-sm font-heading font-semibold flex items-center gap-2 mb-2">
              <MessageCircle size={16} className="text-cyan-400" />
              {lang === 'es' ? 'Consultas de contratación' : 'Booking inquiries'}
            </h2>
            <div className="space-y-1 max-h-40 overflow-y-auto">
              {myInquireOffers.map((o) => (
                <div key={o.id} className="flex items-center justify-between text-xs p-2 rounded-lg bg-pulse-elevated border border-pulse-border/60">
                  <span className="text-pulse-muted truncate">{o.artist_name || o.id} · {o.status}</span>
                  <Link to={`/inquire/${o.id}`} className="text-pulse-primary font-medium shrink-0">
                    {lang === 'es' ? 'Abrir' : 'Open'}
                  </Link>
                </div>
              ))}
            </div>
          </div>
        )}

        <Link to="/library" data-testid="open-library-link" className="w-full mb-4 flex items-center justify-between p-4 bg-pulse-surface border border-pulse-border rounded-xl hover:border-cyan-500/30 transition-colors duration-200">
          <div className="flex items-center gap-3">
            <Library size={18} className="text-pulse-primary" />
            <div className="text-left">
              <p className="text-sm font-medium">{lang === 'es' ? 'Mi colección musical' : 'My music library'}</p>
              <p className="text-xs text-pulse-muted">{lang === 'es' ? 'Artistas guardados, escucha reciente y playlists' : 'Saved artists, recent listening and playlists'}</p>
            </div>
          </div>
          <span className="text-xs text-pulse-primary">{lang === 'es' ? 'Abrir' : 'Open'}</span>
        </Link>

        {/* Favorites */}
        {favorites.length > 0 && (
          <div className="mb-6">
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-heading font-semibold text-sm flex items-center gap-2"><Heart size={14} className="text-red-400" /> {lang === 'es' ? 'Artistas guardados' : 'Saved artists'}</h2>
              <Link to="/library" className="text-xs text-pulse-primary">{lang === 'es' ? 'Ver colección' : 'View library'}</Link>
            </div>
            <div className="flex gap-3 overflow-x-auto pb-2">
              {favorites.map(a => (
                <Link key={a.id} to={`/artist/${a.id}`} className="flex-shrink-0 text-center">
                  <img src={a.photo} alt={a.name} className="w-16 h-16 rounded-full object-cover border border-pulse-border" />
                  <p className="text-xs mt-1 truncate w-16">{a.name}</p>
                </Link>
              ))}
            </div>
          </div>
        )}

        {followingArtists.length > 0 && (
          <div className="mb-6">
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-heading font-semibold text-sm flex items-center gap-2"><Music size={14} className="text-cyan-300" /> {lang === 'es' ? 'Artistas que sigues' : 'Artists you follow'}</h2>
              <Link to="/library" className="text-xs text-pulse-primary">{lang === 'es' ? 'Abrir biblioteca' : 'Open library'}</Link>
            </div>
            <div className="grid grid-cols-2 gap-3">
              {followingArtists.slice(0, 4).map((artist) => (
                <Link key={artist.id} to={`/artist/${artist.id}`} data-testid={`following-artist-${artist.id}`} className="rounded-2xl border border-pulse-border bg-pulse-surface p-3 flex items-center gap-3">
                  {artist.photo ? <img src={artist.photo} alt={artist.name} className="w-12 h-12 rounded-full object-cover border border-pulse-border" /> : <div className="w-12 h-12 rounded-full bg-pulse-elevated border border-pulse-border" />}
                  <div className="min-w-0">
                    <p className="text-sm truncate">{artist.name}</p>
                    <p className="text-[11px] text-pulse-muted truncate">{(artist.genre || []).join(' · ') || (lang === 'es' ? 'Perfil artista' : 'Artist profile')}</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* Quick Links */}
        <div className="space-y-2">
          <Link to="/wallet" className="flex items-center gap-4 p-4 bg-pulse-surface border border-pulse-border rounded-xl hover:border-cyan-500/30 transition-all duration-200">
            <Wallet size={18} className="text-pulse-primary" /><span className="text-sm">{lang === 'es' ? 'Mi Monedero' : 'My Wallet'}</span>
          </Link>
          <Link to="/artist-dashboard" className="flex items-center gap-4 p-4 bg-pulse-surface border border-pulse-border rounded-xl hover:border-amber-500/30 transition-all duration-200">
            <Music size={18} className="text-amber-400" /><span className="text-sm">{lang === 'es' ? 'Panel de Artista' : 'Artist Dashboard'}</span>
          </Link>
          <Link to="/live" className="flex items-center gap-4 p-4 bg-pulse-surface border border-pulse-border rounded-xl hover:border-red-500/30 transition-all duration-200">
            <Radio size={18} className="text-red-500" /><span className="text-sm">{lang === 'es' ? 'En Vivo' : 'Live'}</span>
          </Link>
          <Link to="/playlists" className="flex items-center gap-4 p-4 bg-pulse-surface border border-pulse-border rounded-xl hover:border-cyan-500/30 transition-all duration-200">
            <ListMusic size={18} className="text-pulse-primary" /><span className="text-sm">{t('nav.playlists')}</span>
          </Link>
          <Link to="/bookings" className="flex items-center gap-4 p-4 bg-pulse-surface border border-pulse-border rounded-xl hover:border-cyan-500/30 transition-all duration-200">
            <Clock size={18} className="text-pulse-primary" /><span className="text-sm">{t('booking.history')}</span>
          </Link>
          {(user.role === 'admin' || user.role === 'pluka') && (
            <>
              <Link to="/dashboard" className="flex items-center gap-4 p-4 bg-pulse-surface border border-pulse-border rounded-xl hover:border-cyan-500/30 transition-all duration-200">
                <LayoutDashboard size={18} className="text-pulse-primary" /><span className="text-sm">{t('dashboard.title')}</span>
              </Link>
              <Link to="/analytics" className="flex items-center gap-4 p-4 bg-pulse-surface border border-pulse-border rounded-xl hover:border-cyan-500/30 transition-all duration-200">
                <BarChart3 size={18} className="text-pulse-primary" /><span className="text-sm">{t('analytics.title')}</span>
              </Link>
            </>
          )}
        </div>

        <button data-testid="profile-logout" onClick={logout} className="w-full mt-8 flex items-center justify-center gap-2 p-4 bg-red-500/10 border border-red-500/20 rounded-xl text-red-400 hover:bg-red-500/20 transition-colors duration-200">
          <LogOut size={18} /> {t('auth.logout')}
        </button>

        {/* GDPR Section */}
        <div className="mt-6 p-4 bg-pulse-surface border border-pulse-border rounded-xl space-y-3">
          <h3 className="text-xs text-pulse-muted uppercase tracking-wider">{lang === 'es' ? 'Tus datos (RGPD)' : 'Your data (GDPR)'}</h3>
          <div className="flex gap-2">
            <button data-testid="export-data-btn" onClick={async () => {
              const res = await fetch(`${API}/api/account/export`, { headers: { Authorization: `Bearer ${token}` } });
              const data = await res.json();
              const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
              const url = URL.createObjectURL(blob);
              const a = document.createElement('a');
              a.href = url; a.download = 'pulsemusic-data-export.json'; a.click();
              URL.revokeObjectURL(url);
            }} className="flex-1 flex items-center justify-center gap-2 p-3 bg-pulse-elevated border border-pulse-border rounded-xl text-sm hover:border-cyan-500/30 transition-colors">
              <Shield size={14} className="text-pulse-primary" /> {lang === 'es' ? 'Descargar mis datos' : 'Download my data'}
            </button>
            <button data-testid="delete-account-btn" onClick={async () => {
              const confirm1 = window.confirm(lang === 'es' ? 'Estas seguro? Esta accion es irreversible. Se eliminaran todos tus datos.' : 'Are you sure? This is irreversible.');
              if (!confirm1) return;
              const confirm2 = window.prompt(lang === 'es' ? 'Escribe DELETE para confirmar:' : 'Type DELETE to confirm:');
              if (confirm2 !== 'DELETE') return;
              const res = await fetch(`${API}/api/account/delete`, { method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }, body: JSON.stringify({ confirmation: 'DELETE' }) });
              if (res.ok) { logout(); }
              else { const d = await res.json(); alert(d.detail); }
            }} className="flex items-center justify-center gap-2 p-3 bg-red-500/5 border border-red-500/20 rounded-xl text-sm text-red-400 hover:bg-red-500/10 transition-colors">
              <Trash2 size={14} /> {lang === 'es' ? 'Eliminar cuenta' : 'Delete account'}
            </button>
          </div>
          <p className="text-[10px] text-pulse-muted">{lang === 'es' ? 'Puedes descargar todos tus datos o eliminar tu cuenta conforme al RGPD.' : 'Download your data or delete your account per GDPR.'}</p>
          <div className="flex gap-2">
            <a href="/legal?page=privacy" className="text-[10px] text-pulse-muted hover:text-pulse-primary">{lang === 'es' ? 'Privacidad' : 'Privacy'}</a>
            <a href="/legal?page=terms" className="text-[10px] text-pulse-muted hover:text-pulse-primary">{lang === 'es' ? 'Terminos' : 'Terms'}</a>
          </div>
        </div>
      </div>
    </div>
  );
}
